/**
 * SG Incorporation (Pre) — 문서별 [[Signers]] 컨텍스트.
 *
 * Pre 단계 엔티티는 L_ENTITY 가 아직 없어 icpCd 로 officer API 를 치면 AUTH002 → 리다이렉트됨.
 * Signers UI 에는 entityCd 를 넘기지 않고, 폼의 directors 로 시드 + 수동 추가만 사용 (MY Inc Doc 패턴).
 */
import { inject, provide, reactive, ref, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export type SgPreSignersCtx = {
  /** SignersField 용 — Pre 에서는 항상 '' (officer API 미호출) */
  entityCd: Ref<string>;
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  /** Create Task / gather 용 스냅샷 */
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  /** 비어 있는 문서 Signers 에 directors 시드 */
  seedFromValuesIfEmpty: (values: Record<string, any>) => void;
};

export const SG_PRE_SIGNERS: InjectionKey<SgPreSignersCtx> = Symbol('sgIncPreSigners');

/** Engagement / Constitution / Client Acceptance / ACRA Risk Signature 문서의 [[Signers]] varKey */
export const SG_PRE_SIGNER_KEYS = {
  engagement: 'engagement_Signers',
  constitution: 'constitution_Signers',
  client: 'client_Signers',
  /** ACRA Recommendation Signature */
  riskRecommend: 'risk_recommend_Signers',
  /** ACRA Approval Signature */
  riskApprove: 'risk_approve_Signers',
} as const;

/** 문서 본문 [[Signers]] 용 — directors 시드 대상 (Risk officer 서명은 별도) */
const SG_PRE_SIGNER_DOC_KEYS = [
  SG_PRE_SIGNER_KEYS.engagement,
  SG_PRE_SIGNER_KEYS.constitution,
  SG_PRE_SIGNER_KEYS.client,
] as const;

function officerSignerEntry(
  id: string,
  name: string,
  title: string,
): SignerEntry {
  return {
    id,
    name,
    title: title || '',
    role: 'officer',
    isManual: true,
    showTitle: !!title,
    _signerLayout: 'box',
  };
}

export function buildSgPreSignersFromValues(values: Record<string, any>): SignerEntry[] {
  const out: SignerEntry[] = [];
  const src = values && typeof values === 'object' ? values : {};
  for (let i = 1; i <= 30; i++) {
    const name = String(
      src[`dir${i}_director_name`] || src[`f45_${i}/director_name`] || '',
    ).trim();
    if (!name) continue;
    out.push({
      id: `sg-pre-dir-${i}`,
      name,
      title: 'Director',
      role: 'director',
      email: String(src[`dir${i}_director_email`] || '').trim(),
      isManual: true,
      showTitle: true,
      _signerLayout: 'box',
    });
  }
  if (!out.length) {
    const bare = String(src.director_name || '').trim();
    if (bare) {
      out.push({
        id: 'sg-pre-dir-1',
        name: bare,
        title: 'Director',
        role: 'director',
        email: String(src.director_email || '').trim(),
        isManual: true,
        showTitle: true,
        _signerLayout: 'box',
      });
    }
  }
  return out;
}

export function provideSgPreSigners(_entityCdIgnored?: Ref<string>): SgPreSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  /** Pre: L_ENTITY 없음 — officer 목록 API 호출 방지 */
  const signerEntityCd = ref('');

  const ctx: SgPreSignersCtx = {
    entityCd: signerEntityCd,
    signersOf: (varKey: string) => store[varKey] || [],
    setSigners: (varKey: string, list: SignerEntry[]) => {
      store[varKey] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) {
        if (v?.length) out[k] = v.map((s) => ({ ...s }));
      }
      return out;
    },
    importAll: (map) => {
      for (const k of Object.keys(store)) delete store[k];
      if (!map || typeof map !== 'object') return;
      for (const [k, v] of Object.entries(map)) {
        if (Array.isArray(v)) store[k] = v.map((s) => ({ ...s }));
      }
    },
    seedFromValuesIfEmpty: (values) => {
      const defaults = buildSgPreSignersFromValues(values);
      if (defaults.length) {
        for (const key of SG_PRE_SIGNER_DOC_KEYS) {
          if (!store[key]?.length) {
            store[key] = defaults.map((s) => ({ ...s }));
          }
        }
      }
      // ACRA Risk Signature — officer 성명으로 시드 (Signers UI)
      const recName = String(values?.recommending_officer_name || '').trim();
      const recTitle = String(values?.recommending_officer_position || '').trim();
      if (recName && !store[SG_PRE_SIGNER_KEYS.riskRecommend]?.length) {
        store[SG_PRE_SIGNER_KEYS.riskRecommend] = [
          officerSignerEntry('sg-pre-rec-officer', recName, recTitle),
        ];
      }
      const appName = String(values?.approving_officer_name || '').trim();
      const appTitle = String(values?.approving_officer_position || '').trim();
      if (appName && !store[SG_PRE_SIGNER_KEYS.riskApprove]?.length) {
        store[SG_PRE_SIGNER_KEYS.riskApprove] = [
          officerSignerEntry('sg-pre-app-officer', appName, appTitle),
        ];
      }
    },
  };

  provide(SG_PRE_SIGNERS, ctx);
  return ctx;
}

export function useSgPreSigners(): SgPreSignersCtx {
  const ctx = inject(SG_PRE_SIGNERS, null);
  if (!ctx) {
    const emptyCd = ref('');
    return {
      entityCd: emptyCd,
      signersOf: () => [],
      setSigners: () => {},
      exportAll: () => ({}),
      importAll: () => {},
      seedFromValuesIfEmpty: () => {},
    };
  }
  return ctx;
}
